import React, { useEffect } from "react";
import { Line, Circle } from "react-konva";

export default function WallColumnAsym({
    position,
    lanterns,
    colorList,
    luminaires,
    props,
    rotateAround,
    barCenter,
    centerOffset,
    barThickness,
    lineRefs,
}) {
    useEffect(() => {
        if (props.mountingType !== "wall") return;

        const columnAngle = props.positionAngle ?? 0;
        const minAngle = (columnAngle - 90 + 360) % 360;
        const maxAngle = (columnAngle + 90) % 360;

        const offsets = props.lanterns.map((l) => parseFloat(l.offset) || 0);
        const minOffset = Math.min(...offsets);
        const maxOffset = Math.max(...offsets);
        const centerOffset = (minOffset + maxOffset) / 2;
        const barThickness = 0.4;

        const barCenter = { x: position.x, y: position.y };

        const rotateAround = (point, center, angleRad) => {
            const translated = {
                x: point.x - center.x,
                y: point.y - center.y,
            };
            const rotated = {
                x: translated.x * Math.cos(angleRad) - translated.y * Math.sin(angleRad),
                y: translated.x * Math.sin(angleRad) + translated.y * Math.cos(angleRad),
            };
            return {
                x: center.x + rotated.x,
                y: center.y + rotated.y,
            };
        };

        const angleDiff = (a, b) => {
            let d = a - b;
            while (d > 180) d -= 360;
            while (d < -180) d += 360;
            return d;
        };

        const clampAngle = (angle, min, max) => {
            const diffToMin = Math.abs(angleDiff(angle, min));
            const diffToMax = Math.abs(angleDiff(angle, max));
            return diffToMin < diffToMax ? min : max;
        };

        const updatedLanterns = props.lanterns.map((lantern) => {
            const currentAngle = lantern.positionAngle ?? 0;

            const isAllowed =
                minAngle < maxAngle
                    ? currentAngle >= minAngle && currentAngle <= maxAngle
                    : currentAngle >= minAngle || currentAngle <= maxAngle;

            if (!isAllowed) {
                const clamped = clampAngle(currentAngle, minAngle, maxAngle);
                const beamLength = lantern.beamAimingInfo?.beamLength ?? 0.75;

                const angleRad = ((clamped - 90) * Math.PI) / 180;
                const offset = (parseFloat(lantern.offset) || 0) - centerOffset;
                const armX = offset;
                const armY = -barThickness / 2;

                const baseRot = rotateAround(
                    { x: barCenter.x + armX, y: barCenter.y + armY },
                    barCenter,
                    (columnAngle * Math.PI) / 180
                );

                return {
                    ...lantern,
                    positionAngle: clamped,
                    beamAimingInfo: {
                        ...lantern.beamAimingInfo,
                        beamEndPoint: {
                            x: baseRot.x + beamLength * Math.cos(angleRad),
                            y: baseRot.y + beamLength * Math.sin(angleRad),
                        },
                    },
                };
            }

            return lantern;
        });

        props.updateLanterns(updatedLanterns, props.i);
    }, [props.positionAngle]);

    const isWallMount = props.values?.mountingType === "wall";
    const columnAngle = props.positionAngle || 0;
    const angleRad = (columnAngle * Math.PI) / 180;
    const minAngle = (columnAngle - 90 + 360) % 360;
    const maxAngle = (columnAngle + 90) % 360;

    const angleDiff = (a, b) => {
        let d = a - b;
        while (d > 180) d -= 360;
        while (d < -180) d += 360;
        return d;
    };

    const clampAngle = (angle, min, max) => {
        const diffToMin = Math.abs(angleDiff(angle, min));
        const diffToMax = Math.abs(angleDiff(angle, max));
        return diffToMin < diffToMax ? min : max;
    };

    const clampIfNeeded = (angle) => {
        if (!isWallMount) return angle;
        const isAllowed =
            minAngle < maxAngle
                ? angle >= minAngle && angle <= maxAngle
                : angle >= minAngle || angle <= maxAngle;

        return isAllowed ? angle : clampAngle(angle, minAngle, maxAngle);
    };

    return lanterns.map((lantern, idx) => {
        if (!lineRefs.current[idx]) {
            lineRefs.current[idx] = React.createRef();
        }

        const lineRef = lineRefs.current[idx];
        const offset = (parseFloat(lantern.offset) || 0) - centerOffset;
        const beamLength = lantern.beamLength ?? 0.75;

        const armX = offset;
        const armY = -barThickness / 2;
        const baseRot = rotateAround(
            { x: barCenter.x + armX, y: barCenter.y + armY },
            barCenter,
            angleRad
        );

        const beamAngleRad = ((lantern.positionAngle ?? columnAngle) - 90) * (Math.PI / 180);
        const beamTip = {
            x: baseRot.x + beamLength * Math.cos(beamAngleRad),
            y: baseRot.y + beamLength * Math.sin(beamAngleRad),
        };

        const luminaireIndex = luminaires.indexOf(lantern.luminaireId);
        const resolvedColor =
            luminaireIndex !== -1 && colorList[luminaireIndex]
                ? colorList[luminaireIndex]
                : "lightgray";

        return (
            <React.Fragment key={idx}>
                <Line
                    ref={lineRef}
                    points={[baseRot.x, baseRot.y, beamTip.x, beamTip.y]}
                    stroke={resolvedColor === '#212529' ? "lightgray" : resolvedColor}
                    strokeWidth={3 / props.stageScale}
                />
                <Circle
                    x={beamTip.x}
                    y={beamTip.y}
                    radius={5 / props.stageScale}
                    fill={resolvedColor === '#212529' ? "lightgray" : resolvedColor}
                    draggable={props.selected}
                    onDragMove={(e) => {
                        const { x: dragX, y: dragY } = e.target.position();
                        const dx = dragX - baseRot.x;
                        const dy = dragY - baseRot.y;

                        let angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
                        angleDeg = (angleDeg + 90 + 360) % 360;
                        const clampedAngle = clampIfNeeded(angleDeg);
                        const beamAngleRad = ((clampedAngle - 90) * Math.PI) / 180;

                        const fixedX = baseRot.x + beamLength * Math.cos(beamAngleRad);
                        const fixedY = baseRot.y + beamLength * Math.sin(beamAngleRad);

                        e.target.position({ x: fixedX, y: fixedY });

                        if (lineRef.current) {
                            lineRef.current.points([baseRot.x, baseRot.y, fixedX, fixedY]);
                            lineRef.current.getLayer().batchDraw();
                        }

                        props.updateLanternBeamEndpoint({
                            beamEndPoint: { x: parseFloat(fixedX.toFixed(3)), y: parseFloat(fixedY.toFixed(3)) },
                            beamLength: beamLength,
                            positionAngle: clampedAngle,
                            lanternIndex: idx,
                            columnIndex: props.i,
                        });
                    }}

                    onDragEnd={(e) => {
                        props.doAreaCalc();
                    }}
                />
            </React.Fragment>
        );
    });
}
